import { useEffect, useState } from "react";
import { api } from "@/lib/api";

/** Pill bar with all versions of a project + create/rename/archive/delete. */
export default function VersionsBar({ projectId, onSwitched }) {
  const [versions, setVersions] = useState([]);
  const [active, setActive] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [renaming, setRenaming] = useState(null);   // {id, name}
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // version id pending confirm

  const load = async () => {
    try {
      const r = await api.get(`/icaa/projects/${projectId}/versions`);
      setVersions(r.data.versions || []);
      setActive(r.data.active_version_id || "");
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  const createNew = () => { setNewName(""); setError(""); setCreating(true); };

  const submitCreate = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusy(true); setError("");
    try {
      await api.post(`/icaa/projects/${projectId}/versions`, { name });
      setCreating(false);
      setNewName("");
      await load();
      onSwitched?.();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "");
    } finally { setBusy(false); }
  };

  const switchTo = async (vid) => {
    if (vid === active) return;
    setBusy(true); setError("");
    try {
      await api.put(`/icaa/projects/${projectId}/versions/${vid}/activate`);
      setActive(vid);
      await load();
      onSwitched?.();
    } catch (e) {
      setError(e?.response?.data?.detail || "");
    } finally { setBusy(false); }
  };

  const setArchived = async (v, archived) => {
    setBusy(true); setError("");
    try {
      await api.put(`/icaa/projects/${projectId}/versions/${v.id}/archive`, { archived });
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "");
    } finally { setBusy(false); }
  };

  const removeV = async (v) => {
    setBusy(true); setError("");
    try {
      const r = await api.delete(`/icaa/projects/${projectId}/versions/${v.id}`);
      setConfirmDelete(null);
      if (r.data.active_version_id !== active) onSwitched?.();
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "");
    } finally { setBusy(false); }
  };

  const submitRename = async () => {
    if (!renaming) return;
    const name = renaming.name.trim();
    if (!name) return;
    setBusy(true); setError("");
    try {
      await api.patch(`/icaa/projects/${projectId}/versions/${renaming.id}`, { name });
      setRenaming(null);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "");
    } finally { setBusy(false); }
  };

  if (!versions.length) {
    return <div className="mt-2 text-[11px] text-stone-400">Inicializando versiones…</div>;
  }

  const liveVersions = versions.filter((v) => !v.archived);
  const archivedVersions = versions.filter((v) => v.archived);

  return (
    <div className="mt-3" data-testid="versions-bar">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="mr-1 text-[10px] uppercase tracking-[0.3em] text-stone-500">Versiones</span>
        {liveVersions.map((v) => {
          const isActive = v.id === active;
          return (
            <div key={v.id} className="group inline-flex items-center" data-testid={`version-pill-${v.id}`}>
              <button onClick={() => switchTo(v.id)} disabled={busy}
                      data-testid={`version-switch-${v.id}`}
                      className={`rounded-l-full px-3 py-1 text-[11px] font-semibold transition ${
                        isActive ? "bg-[#26775E] text-white" : "bg-stone-200 text-stone-700 hover:bg-stone-300"
                      }`}>
                {v.name}
              </button>
              <button onClick={() => setRenaming({ id: v.id, name: v.name })} disabled={busy}
                      data-testid={`version-rename-${v.id}`}
                      title="Renombrar"
                      className={`px-1.5 py-1 text-[10px] ${
                        isActive ? "bg-[#26775E] text-white/70 hover:text-white" : "bg-stone-200 text-stone-500 hover:text-stone-900"
                      }`}>✎</button>
              <button onClick={() => setArchived(v, true)} disabled={busy || isActive}
                      data-testid={`version-archive-${v.id}`}
                      title={isActive ? "No puedes archivar la versión activa" : "Archivar"}
                      className={`rounded-r-full px-1.5 py-1 text-[10px] ${
                        isActive ? "bg-[#26775E] text-white/40 cursor-not-allowed" : "bg-stone-200 text-stone-500 hover:text-amber-700"
                      }`}>▼</button>
            </div>
          );
        })}
        <button onClick={createNew} disabled={busy} data-testid="version-create-btn"
                className="ml-1 rounded-full border border-dashed border-[#26775E] px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-[#26775E] hover:bg-[#26775E] hover:text-white">
          + Nueva versión
        </button>
        {archivedVersions.length > 0 && (
          <button onClick={() => setShowArchived((s) => !s)}
                  data-testid="versions-toggle-archived"
                  className="ml-2 rounded-full border border-stone-300 px-3 py-1 text-[10px] uppercase tracking-widest text-stone-600 hover:border-stone-500">
            {showArchived ? "Ocultar archivadas" : `Ver archivadas (${archivedVersions.length})`}
          </button>
        )}
      </div>

      {error && <p className="mt-2 text-[11px] text-red-600" data-testid="versions-error">{error}</p>}

      {creating && (
        <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="version-create-row">
          <input value={newName} onChange={(e) => setNewName(e.target.value)}
                 onKeyDown={(e) => { if (e.key === "Enter") submitCreate(); if (e.key === "Escape") setCreating(false); }}
                 autoFocus
                 placeholder="Ej: v2 — para Eurimages diciembre 2026"
                 data-testid="version-create-input"
                 className="min-w-[260px] flex-1 rounded border border-stone-300 bg-white px-3 py-1 text-sm outline-none focus:border-[#26775E]" />
          <button onClick={submitCreate} disabled={busy || !newName.trim()}
                  data-testid="version-create-save"
                  className="rounded-full bg-[#26775E] px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-white hover:bg-[#1c5c48] disabled:opacity-40">
            {busy ? "Creando…" : "Crear versión"}
          </button>
          <button onClick={() => { setCreating(false); setError(""); }}
                  className="text-[11px] text-stone-500 hover:text-stone-900">Cancelar</button>
        </div>
      )}

      {renaming && (
        <div className="mt-2 flex items-center gap-2" data-testid="version-rename-row">
          <input value={renaming.name} onChange={(e) => setRenaming({ ...renaming, name: e.target.value })}
                 onKeyDown={(e) => { if (e.key === "Enter") submitRename(); if (e.key === "Escape") setRenaming(null); }}
                 autoFocus
                 data-testid="version-rename-input"
                 className="rounded border border-stone-300 bg-white px-3 py-1 text-sm outline-none focus:border-[#26775E]" />
          <button onClick={submitRename} disabled={busy}
                  data-testid="version-rename-save"
                  className="rounded-full bg-[#26775E] px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-white hover:bg-[#1c5c48]">
            Guardar
          </button>
          <button onClick={() => setRenaming(null)}
                  className="text-[11px] text-stone-500 hover:text-stone-900">Cancelar</button>
        </div>
      )}

      {showArchived && archivedVersions.length > 0 && (
        <div className="mt-3 rounded-lg border border-dashed border-stone-300 bg-stone-50/60 px-3 py-2" data-testid="archived-versions">
          <div className="mb-1.5 text-[10px] uppercase tracking-[0.3em] text-stone-500">Archivadas</div>
          <div className="flex flex-wrap items-center gap-1.5">
            {archivedVersions.map((v) => (
              <div key={v.id} className="inline-flex items-center" data-testid={`archived-pill-${v.id}`}>
                <span className="rounded-l-full bg-stone-300/70 px-3 py-1 text-[11px] font-semibold text-stone-600 line-through">
                  {v.name}
                </span>
                <button onClick={() => switchTo(v.id)} disabled={busy}
                        data-testid={`version-restore-${v.id}`}
                        title="Restaurar y activar"
                        className="bg-stone-300/70 px-2 py-1 text-[10px] text-stone-600 hover:text-[#26775E]">↺ Restaurar</button>
                <button onClick={() => setArchived(v, false)} disabled={busy}
                        data-testid={`version-unarchive-${v.id}`}
                        title="Desarchivar (sin activar)"
                        className="bg-stone-300/70 px-2 py-1 text-[10px] text-stone-600 hover:text-stone-900">▲</button>
                {confirmDelete === v.id ? (
                  <>
                    <button onClick={() => removeV(v)} disabled={busy}
                            data-testid={`version-delete-confirm-${v.id}`}
                            className="rounded-r-full bg-red-600 px-2 py-1 text-[10px] font-semibold uppercase text-white hover:bg-red-700">
                      Confirmar borrado
                    </button>
                    <button onClick={() => setConfirmDelete(null)}
                            className="ml-1 text-[10px] text-stone-500 hover:text-stone-900">Cancelar</button>
                  </>
                ) : (
                  <button onClick={() => setConfirmDelete(v.id)} disabled={busy || versions.length === 1}
                          data-testid={`version-delete-${v.id}`}
                          title="Borrar definitivamente"
                          className="rounded-r-full bg-stone-300/70 px-2 py-1 text-[10px] text-stone-600 hover:text-red-600">×</button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
